import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useLocalePath from "../i18n/useLocalePath";

const BASE = "https://www.nekochan.app";
const DEFAULT_IMAGE = `${BASE}/social-cover.png`;

export default function Meta({ titleKey, descKey, noIndex, enOnly }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // Derive the locale from the URL, not i18n.language: on first render the
  // language may not have synced to the path yet (see Layout.jsx).
  const { isSpanish: isEs } = useLocalePath();

  const title = titleKey ? t(titleKey) : t("meta.title");
  const description = descKey ? t(descKey) : t("meta.description");

  // Strip the /es prefix and any trailing slash so /faq and /faq/ produce the
  // same canonical (Amplify serves both), matching the sitemap and prerender.
  const basePath = pathname.replace(/^\/es/, "").replace(/\/+$/, "") || "/";
  const enUrl = BASE + basePath;
  const esUrl = BASE + "/es" + basePath;
  const canonical = enOnly ? enUrl : isEs ? esUrl : enUrl;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />

      <link rel="canonical" href={canonical} />
      {/* Each <link> must be a direct child of <Helmet>: react-helmet-async
          does not walk React fragments, so wrapping these in <>...</> silently
          dropped every hreflang tag. */}
      {!enOnly && <link rel="alternate" href={enUrl} hrefLang="en" />}
      {!enOnly && <link rel="alternate" href={esUrl} hrefLang="es" />}
      {!enOnly && <link rel="alternate" href={enUrl} hrefLang="x-default" />}

      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="Nekochan" />
      <meta property="og:locale" content={isEs ? "es_LA" : "en_US"} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
      <meta name="twitter:site" content="@NekochanApp" />
    </Helmet>
  );
}
